import { useMemo } from 'react'
import { Line, Bar } from 'react-chartjs-2'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Alert
} from '@mui/material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js'
import InfoIcon from '@mui/icons-material/Info'
import ScienceIcon from '@mui/icons-material/Science'
import { useState } from 'react'
import {
  ashraeDegradation,
  corpusDegradation,
  DEGRADATION_BY_AGE,
  AGE_DATA_SAMPLE_SIZE
} from '../../services/degradationCurve'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
)

/**
 * DegradationComparison Chart
 *
 * Visualizes the comparison between ASHRAE theoretical degradation model
 * and the Corpus-derived model based on PE-certified field measurements.
 *
 * Shows:
 * 1. Line chart: Degradation % vs Equipment Age for both models
 * 2. Scatter points: Individual case studies from corpus
 */
function DegradationComparison({ highlightAge = null, title = 'Degradation Model Comparison' }) {
  const [chartType, setChartType] = useState('line')

  // Generate data points for both models
  const chartData = useMemo(() => {
    const ages = Array.from({ length: 26 }, (_, i) => i) // 0-25 years

    const ashraeData = ages.map(age => ({
      x: age,
      y: ashraeDegradation(age) * 100
    }))

    const corpusData = ages.map(age => ({
      x: age,
      y: corpusDegradation(age) * 100
    }))

    // Extract actual data points from corpus buckets
    const corpusPoints = []
    Object.entries(DEGRADATION_BY_AGE).forEach(([bucket, data]) => {
      const [minAge, maxAge] = bucket === '20+' ? [20, 25] : bucket.split('-').map(Number)
      const midAge = (minAge + maxAge) / 2
      corpusPoints.push({
        x: midAge,
        y: data.avgDegradation,
        n: data.n,
        bucket,
        stdDev: data.stdDev
      })
    })

    return {
      labels: ages,
      datasets: [
        {
          label: `Corpus Model (n=${AGE_DATA_SAMPLE_SIZE})`,
          data: corpusData,
          borderColor: 'rgba(76, 175, 80, 1)',
          backgroundColor: 'rgba(76, 175, 80, 0.1)',
          borderWidth: 3,
          fill: true,
          tension: 0.4,
          pointRadius: 0
        },
        {
          label: 'ASHRAE Model (~2%/yr)',
          data: ashraeData,
          borderColor: 'rgba(33, 150, 243, 1)',
          backgroundColor: 'rgba(33, 150, 243, 0.1)',
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          tension: 0,
          pointRadius: 0
        },
        {
          label: 'Corpus Data Points',
          data: corpusPoints,
          type: 'scatter',
          borderColor: 'rgba(76, 175, 80, 1)',
          backgroundColor: 'rgba(76, 175, 80, 0.8)',
          pointRadius: 8,
          pointHoverRadius: 12,
          showLine: false
        }
      ]
    }
  }, [])

  // Bar chart data for bucket comparison
  const barChartData = useMemo(() => {
    const buckets = Object.keys(DEGRADATION_BY_AGE)
    const corpusValues = buckets.map(b => DEGRADATION_BY_AGE[b].avgDegradation)

    // Calculate ASHRAE equivalents for bucket midpoints
    const ashraeValues = buckets.map(bucket => {
      const [minAge, maxAge] = bucket === '20+' ? [20, 25] : bucket.split('-').map(Number)
      const midAge = (minAge + maxAge) / 2
      return ashraeDegradation(midAge) * 100
    })

    return {
      labels: buckets.map(b => `${b} yrs`),
      datasets: [
        {
          label: 'Corpus (Measured)',
          data: corpusValues,
          backgroundColor: 'rgba(76, 175, 80, 0.7)',
          borderColor: 'rgba(76, 175, 80, 1)',
          borderWidth: 2
        },
        {
          label: 'ASHRAE (Theoretical)',
          data: ashraeValues,
          backgroundColor: 'rgba(33, 150, 243, 0.7)',
          borderColor: 'rgba(33, 150, 243, 1)',
          borderWidth: 2
        }
      ]
    }
  }, [])

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      datalabels: {
        display: false
      },
      legend: {
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const point = context.raw
            if (point.n) {
              return `${context.dataset.label}: ${point.y.toFixed(1)}% (n=${point.n}, ±${point.stdDev}%)`
            }
            return `${context.dataset.label}: ${point.y?.toFixed(1) || context.parsed.y.toFixed(1)}%`
          },
          afterBody: (tooltipItems) => {
            const age = tooltipItems[0]?.parsed?.x
            if (age !== undefined) {
              const diff = (corpusDegradation(age) - ashraeDegradation(age)) * 100
              if (Math.abs(diff) > 0.5) {
                return [`Difference: ${diff > 0 ? '+' : ''}${diff.toFixed(1)}%`]
              }
            }
            return []
          }
        }
      },
      annotation: highlightAge ? {
        annotations: {
          line1: {
            type: 'line',
            xMin: highlightAge,
            xMax: highlightAge,
            borderColor: 'rgba(255, 152, 0, 0.8)',
            borderWidth: 2,
            borderDash: [6, 6],
            label: {
              display: true,
              content: `Age: ${highlightAge}`,
              position: 'start'
            }
          }
        }
      } : {}
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Equipment Age (Years)'
        },
        min: 0,
        max: 25
      },
      y: {
        title: {
          display: true,
          text: 'Thermal Degradation (%)'
        },
        min: 0,
        max: 35,
        ticks: {
          callback: (value) => `${value}%`
        }
      }
    }
  }

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: {
        display: false
      },
      legend: {
        position: 'top'
      },
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            const bucket = Object.keys(DEGRADATION_BY_AGE)[context.dataIndex]
            const data = DEGRADATION_BY_AGE[bucket]
            if (context.datasetIndex === 0) {
              return [`n=${data.n} studies`, `±${data.stdDev}% std dev`]
            }
            return []
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Degradation (%)'
        },
        ticks: {
          callback: (value) => `${value}%`
        }
      }
    }
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScienceIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
            <Chip label={`n=${AGE_DATA_SAMPLE_SIZE}`} size="small" />
          </Box>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(_, v) => v && setChartType(v)}
            size="small"
          >
            <ToggleButton value="line">Curve</ToggleButton>
            <ToggleButton value="bar">Buckets</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Key Insight */}
        <Alert severity="success" sx={{ mb: 2 }} icon={<InfoIcon />}>
          <Typography variant="body2">
            <strong>Key Insight:</strong> Real-world degradation (Corpus) exceeds ASHRAE estimates during
            the 0-15 year window. After 15 years, observed degradation appears lower due to survivorship
            bias—severely degraded equipment is typically replaced rather than measured.
          </Typography>
        </Alert>

        {/* Chart */}
        <Box sx={{ height: 300 }}>
          {chartType === 'line' ? (
            <Line data={chartData} options={lineOptions} />
          ) : (
            <Bar data={barChartData} options={barOptions} />
          )}
        </Box>

        {/* Bucket Summary */}
        <Grid container spacing={1} sx={{ mt: 2 }}>
          {Object.entries(DEGRADATION_BY_AGE).map(([bucket, data]) => (
            <Grid item xs={12} sm={2.4} key={bucket}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 1,
                  bgcolor: (theme) => bucket === '10-15'
                    ? 'rgba(76, 175, 80, 0.15)'
                    : theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)',
                  borderRadius: 1,
                  border: bucket === '10-15' ? '2px solid' : '1px solid',
                  borderColor: bucket === '10-15' ? 'success.main' : 'divider'
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  {bucket} yrs
                </Typography>
                <Typography variant="h6" color={bucket === '10-15' ? 'success.main' : 'text.primary'}>
                  {data.avgDegradation.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  n={data.n}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Methodology Note */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: (theme) => theme.palette.mode === 'light' ? 'grey.100' : 'rgba(255,255,255,0.05)', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Methodology:</strong> Corpus model derived from {AGE_DATA_SAMPLE_SIZE} PE-certified
            before/after measurements where equipment age was documented. ASHRAE model assumes 2% COP
            decline per year (UCF/FSEC study) with 30% maximum. The "improvement %" from PermaFrost NMR
            treatment directly measures existing thermal degradation since treatment restores near-100%
            of lost efficiency.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default DegradationComparison
